/**
 * The payment envelope — how much a member may receive in a period, and why.
 *
 * THE ARITHMETIC IS THE POINT OF THE SCREEN.
 * $1,500 a year paid evenly is $375 a federal fiscal quarter against SNAP's
 * $300 ceiling. The overshoot is printed before anything is saved, because the
 * person configuring this is the only one who can fix it — by shaping the
 * schedule or lowering the envelope — and after the first payment it is a
 * deferral queue instead of a decision.
 *
 * THE ENFORCEMENT TOGGLE IS NOT A SWITCH.
 * `block` defers a breaching payment to the next period; `warn` pays it and
 * records the breach. Those are not symmetrical: a deferred payment can be
 * released later by moving to `warn`, but a payment already made above the
 * ceiling cannot be taken back out of a SNAP determination. So moving to
 * `warn` asks for a typed confirmation and states the consequence; moving back
 * to `block` does not.
 *
 * `cap_basis` is required and free text. A ceiling with no stated reason is a
 * number somebody optimises later.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, Check, Loader2, Scale } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { formatDateOnly } from '../../utils/dateTime';
import {
  PERIOD_LABELS,
  benefitsApi,
  type BenefitRule,
  type EnvelopeInput,
  type EnvelopeResult,
  type PaymentEnvelope,
} from './types';

const money = (v: string | number | null | undefined) =>
  v == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v));

const PERIODS_PER_YEAR: Record<PaymentEnvelope['period_type'], number> = {
  federal_fiscal_quarter: 4,
  calendar_quarter: 4,
  month: 12,
  year: 1,
};

interface Props {
  programId: number;
  stateCode?: string;
  readOnly?: boolean;
}

export const ProgramPaymentEnvelope: React.FC<Props> = ({
  programId,
  stateCode = 'MO',
  readOnly,
}) => {
  const [result, setResult] = useState<EnvelopeResult | null>(null);
  const [rules, setRules] = useState<BenefitRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState<EnvelopeInput>({
    annual_envelope: null,
    period_type: 'federal_fiscal_quarter',
    period_cap: 0,
    cap_basis: '',
    benefit_rule_id: null,
    enforcement: 'block',
  });
  // Typed confirmation for the one irreversible direction.
  const [warnConfirm, setWarnConfirm] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [env, ruleSet] = await Promise.all([
        benefitsApi.getEnvelope(programId),
        benefitsApi.listRules(stateCode).catch(() => ({ rules: [] as BenefitRule[], state_code: stateCode })),
      ]);
      setResult(env);
      setRules(ruleSet.rules);
      if (env.envelope) {
        setDraft({
          annual_envelope: env.envelope.annual_envelope ? Number(env.envelope.annual_envelope) : null,
          period_type: env.envelope.period_type,
          period_cap: Number(env.envelope.period_cap),
          cap_basis: env.envelope.cap_basis,
          benefit_rule_id: env.envelope.benefit_rule_id,
          enforcement: env.envelope.enforcement,
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the envelope.');
    } finally {
      setLoading(false);
    }
  }, [programId, stateCode]);

  useEffect(() => {
    void load();
  }, [load]);

  // Rules that actually carry a ceiling — the only ones worth deriving a cap from.
  const cappedRules = useMemo(() => rules.filter((r) => r.period_cap != null), [rules]);

  const periods = PERIODS_PER_YEAR[draft.period_type];
  const perPeriod = draft.annual_envelope ? draft.annual_envelope / periods : null;
  const overshoot = perPeriod != null && draft.period_cap > 0 && perPeriod > draft.period_cap
    ? perPeriod - draft.period_cap
    : null;
  const envelopeThatFits = draft.period_cap > 0 ? draft.period_cap * periods : null;

  const existing = result?.envelope ?? null;
  const movingToWarn = existing?.enforcement !== 'warn' && draft.enforcement === 'warn';
  const warnBlocked = movingToWarn && warnConfirm.trim().toUpperCase() !== 'PAY OVER THE CEILING';

  const useRuleCap = (rule: BenefitRule) => {
    setDraft((d) => ({
      ...d,
      period_cap: Number(rule.period_cap),
      period_type:
        (rule.period_type as PaymentEnvelope['period_type']) ?? d.period_type,
      benefit_rule_id: rule.id,
      cap_basis:
        d.cap_basis.trim() ||
        `${rule.program_name} (${rule.program_code}): ${rule.explanation} (${rule.citation})`,
    }));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      setResult(await benefitsApi.saveEnvelope(programId, draft));
      setSaved(true);
      setWarnConfirm('');
      window.setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the envelope.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        Loading the payment envelope…
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Payment envelope</CardTitle>
        <p className="text-sm text-muted-foreground">
          A ceiling on what one member may receive in a period, so a payment cannot breach a
          benefit rule by accident. Leave it unset for a programme with no benefit exposure.
        </p>
      </CardHeader>

      <CardContent className="space-y-5">
        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {existing && (
          <p className="text-xs text-muted-foreground">
            In force since {formatDateOnly(existing.effective_from)}
            {existing.rule_citation && <> · derived from {existing.rule_citation}</>}
          </p>
        )}

        {cappedRules.length > 0 && (
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <p className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Scale className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              Take the ceiling from a rule
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Deriving it keeps the number and the regulation together, so a reader can get from
              the ceiling to its authority without asking anyone.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {cappedRules.map((rule) => (
                <Button
                  key={rule.id}
                  size="sm"
                  variant={draft.benefit_rule_id === rule.id ? 'default' : 'outline'}
                  onClick={() => useRuleCap(rule)}
                  disabled={readOnly}
                >
                  {rule.program_code} · {money(rule.period_cap)}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="annual-envelope">Annual envelope</Label>
            <Input
              id="annual-envelope"
              inputMode="decimal"
              value={draft.annual_envelope ?? ''}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  annual_envelope: e.target.value === '' ? null : Number(e.target.value),
                }))
              }
              disabled={readOnly}
            />
            <p className="mt-1 text-xs text-muted-foreground">What one member receives in a year.</p>
          </div>

          <div>
            <Label htmlFor="period-cap">Ceiling per period</Label>
            <Input
              id="period-cap"
              inputMode="decimal"
              value={draft.period_cap || ''}
              onChange={(e) => setDraft((d) => ({ ...d, period_cap: Number(e.target.value) }))}
              disabled={readOnly}
            />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="period-type">Period</Label>
            <select
              id="period-type"
              className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground disabled:opacity-60"
              value={draft.period_type}
              onChange={(e) =>
                setDraft((d) => ({ ...d, period_type: e.target.value as PaymentEnvelope['period_type'] }))
              }
              disabled={readOnly}
            >
              {(Object.keys(PERIOD_LABELS) as PaymentEnvelope['period_type'][]).map((k) => (
                <option key={k} value={k}>
                  {PERIOD_LABELS[k]}
                </option>
              ))}
            </select>
            {draft.period_type === 'federal_fiscal_quarter' && (
              <p className="mt-1 text-xs text-muted-foreground">
                Starts 1 October, not 1 January. Treating it as a calendar quarter moves every
                boundary by a month.
              </p>
            )}
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="cap-basis">Why this ceiling exists</Label>
            <Textarea
              id="cap-basis"
              rows={3}
              value={draft.cap_basis}
              onChange={(e) => setDraft((d) => ({ ...d, cap_basis: e.target.value }))}
              disabled={readOnly}
              placeholder="Name the rule and what happens above it."
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Required. A ceiling with no stated reason is a number somebody optimises later.
            </p>
          </div>
        </div>

        {/* The arithmetic, before anything is saved. */}
        {perPeriod != null && draft.period_cap > 0 && (
          <div
            className={`rounded-lg border p-3 ${
              overshoot
                ? 'border-orange-300 bg-orange-50 dark:border-orange-800 dark:bg-orange-950'
                : 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950'
            }`}
          >
            <p className="text-sm tabular-nums text-foreground">
              {money(draft.annual_envelope)} ÷ {periods} ={' '}
              <strong>{money(perPeriod)}</strong> per period against a {money(draft.period_cap)}{' '}
              ceiling.
            </p>
            {overshoot ? (
              <p className="mt-2 text-sm text-orange-900 dark:text-orange-100">
                An even schedule overshoots by <strong>{money(overshoot)}</strong> every period, so
                payments will be deferred. Shaping the schedule, or dropping the envelope to{' '}
                <strong>{money(envelopeThatFits)}</strong>, removes the deferrals.
              </p>
            ) : (
              <p className="mt-2 text-sm text-emerald-900 dark:text-emerald-100">
                An even schedule fits under the ceiling.
              </p>
            )}
          </div>
        )}

        {/* Enforcement */}
        <fieldset className="rounded-lg border border-border p-3">
          <legend className="px-1 text-sm font-medium text-foreground">
            When a payment would breach the ceiling
          </legend>
          <div className="mt-2 space-y-2">
            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 has-[:checked]:border-sky-500 has-[:checked]:bg-sky-50 dark:has-[:checked]:bg-sky-950">
              <input
                type="radio"
                className="mt-1"
                name="enforcement"
                checked={draft.enforcement === 'block'}
                onChange={() => setDraft((d) => ({ ...d, enforcement: 'block' }))}
                disabled={readOnly}
              />
              <span className="text-sm">
                <span className="flex items-center gap-1.5 font-medium text-foreground">
                  <CalendarClock className="h-4 w-4" aria-hidden="true" />
                  Move it to the next period
                </span>
                <span className="mt-1 block text-muted-foreground">
                  She does not lose the money. This is the reversible choice — a deferred payment
                  can be released later by switching to the other option.
                </span>
              </span>
            </label>

            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 has-[:checked]:border-orange-500 has-[:checked]:bg-orange-50 dark:has-[:checked]:bg-orange-950">
              <input
                type="radio"
                className="mt-1"
                name="enforcement"
                checked={draft.enforcement === 'warn'}
                onChange={() => setDraft((d) => ({ ...d, enforcement: 'warn' }))}
                disabled={readOnly}
              />
              <span className="text-sm">
                <span className="flex items-center gap-1.5 font-medium text-foreground">
                  <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                  Pay it anyway and record the breach
                </span>
                <span className="mt-1 block text-muted-foreground">
                  A payment already made above the ceiling cannot be taken back out of a benefit
                  determination. This one does not undo.
                </span>
              </span>
            </label>
          </div>

          {movingToWarn && (
            <div className="mt-3 rounded-md border border-orange-300 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-950">
              <Label htmlFor="warn-confirm" className="text-orange-900 dark:text-orange-100">
                Type <strong>PAY OVER THE CEILING</strong> to confirm
              </Label>
              <Input
                id="warn-confirm"
                className="mt-2"
                value={warnConfirm}
                onChange={(e) => setWarnConfirm(e.target.value)}
                autoComplete="off"
              />
              <p className="mt-2 text-xs text-orange-900 dark:text-orange-100">
                Payments above {money(draft.period_cap)} per period will reach members and count
                against their benefits.
              </p>
            </div>
          )}
        </fieldset>

        {!readOnly && (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={() => void save()}
              disabled={saving || warnBlocked || !draft.cap_basis.trim() || draft.period_cap <= 0}
            >
              {saving ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : saved ? (
                <Check className="mr-1 h-4 w-4" aria-hidden="true" />
              ) : null}
              {saved ? 'Saved' : existing ? 'Amend the envelope' : 'Set the envelope'}
            </Button>
            {existing && (
              <p className="text-xs text-muted-foreground">
                An amendment closes the current envelope and opens a new one, so a payment
                deferred under the old ceiling stays explainable.
              </p>
            )}
          </div>
        )}

        {!!result?.open_deferrals && (
          <p className="flex items-center gap-2 border-t border-border pt-3 text-sm text-foreground">
            <CalendarClock className="h-4 w-4 text-sky-600" aria-hidden="true" />
            {result.open_deferrals} payment{result.open_deferrals === 1 ? ' is' : 's are'} currently
            held out of a full period.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default ProgramPaymentEnvelope;
